// Notemaker — Task Management → Notemaker (/notes, /notes/:noteId).
//
// Personal rich-text notes. Landing = gallery of cards with a filter rail;
// opening a note turns the gallery into a compact column beside the editor
// (phones show one pane at a time). Edits autosave ~1s after the last
// keystroke; pin / colour / tags / archive apply immediately. Trash keeps
// deleted notes restorable until emptied. A note can be shared with
// colleagues, who can edit its title and body; theirs show up under "All
// notes" and "Shared with me", and every edit is in the note's history.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  Keyboard,
  LayoutGrid,
  Loader2,
  NotebookPen,
  Pin,
  PinOff,
  Plus,
  RotateCcw,
  Rows3,
  Search,
  StickyNote,
  Trash2,
  Users,
  X,
} from "lucide-react";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import notesApi, {
  type Note,
  type NoteColor,
  type NoteCounts,
  type NotePatch,
  type NoteScope,
  type NoteShareUser,
  type NoteSummary,
  type NoteTag,
} from "../../api/notes";
import { todoApi } from "../../api/todo";
import NoteEditor, { type NoteAction, type SaveState } from "./NoteEditor";
import NoteList, { type NoteSort } from "./NoteList";
import ShareNoteDialog from "./ShareNoteDialog";
import {
  NOTE_SWATCHES,
  countWords,
  downloadTextFile,
  fileSafeName,
  htmlToMarkdown,
  htmlToText,
  isBlankHtml,
} from "./noteUtils";
import "./notemaker.css";

const AUTOSAVE_MS = 900;
// Undo toasts stay a little longer than sonner's 4 s default — reading
// "Moved to Trash", deciding, and reaching the button takes a moment.
const UNDO_TOAST_MS = 6000;
const VIEW_KEY = "nm:view";
const SORT_KEY = "nm:sort";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

// One entry per scope chip at the top of the page (compact, like the Tasks
// page status chips, so a note with a big table keeps its height). `tone`
// picks the chip colour; `hint` is its tooltip. Pinned has no chip: pinned
// notes simply sit on top of "All notes".
const SCOPES: { key: NoteScope; label: string; icon: React.ReactNode; countKey: keyof NoteCounts; hint: string; tone: string }[] = [
  { key: "all", label: "All notes", icon: <StickyNote size={14} aria-hidden="true" />, countKey: "all", hint: "Your notes and notes shared with you", tone: "all" },
  { key: "shared", label: "Shared with me", icon: <Users size={14} aria-hidden="true" />, countKey: "shared", hint: "Colleagues' notes — you can edit them", tone: "shared" },
  { key: "archived", label: "Archived", icon: <Archive size={14} aria-hidden="true" />, countKey: "archived", hint: "Tidied away", tone: "archived" },
  { key: "trash", label: "Trash", icon: <Trash2 size={14} aria-hidden="true" />, countKey: "trash", hint: "Restorable until emptied", tone: "trash" },
];

const readPref = <T extends string>(key: string, fallback: T, allowed: T[]): T => {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};
const writePref = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode — preference just won't stick */
  }
};

const summaryOf = (n: Note): NoteSummary => {
  const { content, ...rest } = n;
  return rest;
};

const isTypingTarget = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable;
};

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface PendingSave {
  id: string;
  patch: { title?: string; content?: string };
}

export default function Notemaker() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();

  // ── Filters / preferences ────────────────────────────────────────────
  const [scope, setScope] = useState<NoteScope>("all");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 250);
  const [tag, setTag] = useState<string | null>(null);
  const [color, setColor] = useState<NoteColor | null>(null);
  const [sort, setSort] = useState<NoteSort>(() => readPref<NoteSort>(SORT_KEY, "updated", ["updated", "created", "title"]));
  const [view, setView] = useState<"grid" | "list">(() => readPref<"grid" | "list">(VIEW_KEY, "grid", ["grid", "list"]));

  // ── Data ─────────────────────────────────────────────────────────────
  const [list, setList] = useState<NoteSummary[]>([]);
  const [counts, setCounts] = useState<NoteCounts>({ all: 0, pinned: 0, archived: 0, trash: 0, shared: 0 });
  const [tags, setTags] = useState<NoteTag[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [shareFor, setShareFor] = useState<NoteSummary | null>(null);

  const cacheRef = useRef<Map<string, Note>>(new Map());
  const pendingRef = useRef<PendingSave | null>(null);
  const timerRef = useRef<number | null>(null);
  const freshIdsRef = useRef<Set<string>>(new Set());   // created this session, still blank
  const prevIdRef = useRef<string | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);
  const hasEverLoadedRef = useRef(false);

  // ── List loading ─────────────────────────────────────────────────────
  const loadList = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent && !hasEverLoadedRef.current) setListLoading(true);
    try {
      const res = await notesApi.list({ scope, q: debouncedQuery, tag: tag || undefined, color: color || undefined });
      setList(res.data.notes);
      setCounts(res.data.counts);
      setTags(res.data.tags);
      setListError(null);
      hasEverLoadedRef.current = true;
    } catch {
      setListError("Couldn't load your notes. Check your connection and try again.");
    } finally {
      setListLoading(false);
    }
  }, [scope, debouncedQuery, tag, color]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Filters changed → leave multi-select.
  useEffect(() => {
    setChecked(new Set());
  }, [scope, debouncedQuery, tag, color]);

  // ── Cache helpers ────────────────────────────────────────────────────
  const applyNote = useCallback((note: Note) => {
    cacheRef.current.set(note.id, note);
    setSelected((prev) => (prev && prev.id === note.id ? note : prev));
    setList((prev) => prev.map((n) => (n.id === note.id ? summaryOf(note) : n)));
  }, []);

  const dropFromList = useCallback((id: string) => {
    setList((prev) => prev.filter((n) => n.id !== id));
    setChecked((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // ── Autosave ─────────────────────────────────────────────────────────
  // Saves run one after another (inflightRef) so two quick flushes can never
  // land out of order and leave the list showing an older snippet.
  const inflightRef = useRef<Promise<void>>(Promise.resolve());
  const doFlush = useCallback(async (): Promise<void> => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Only the note that is open gets the status pill; a save finishing for
    // a note the user already left must not relabel the new one.
    const label = (state: SaveState) => {
      if (prevIdRef.current === pending.id) setSaveState(state);
    };
    label("saving");
    try {
      const res = await notesApi.update(pending.id, pending.patch);
      applyNote(res.data);
      if (res.data.title || !isBlankHtml(res.data.content)) freshIdsRef.current.delete(res.data.id);
      // (cast: TS narrowed the ref to null after the assignment above, but a
      // keystroke during the await may have refilled it.)
      const queued = pendingRef.current as PendingSave | null;
      label(queued?.id === pending.id ? "dirty" : "saved");
      window.setTimeout(() => {
        if (prevIdRef.current === pending.id) setSaveState((s) => (s === "saved" ? "idle" : s));
      }, 2500);
    } catch (err: any) {
      // Keep the edit so the next keystroke (or Retry) sends it again.
      const later = pendingRef.current as PendingSave | null;
      pendingRef.current = later && later.id === pending.id
        ? { id: pending.id, patch: { ...pending.patch, ...later.patch } }
        : pending;
      label("error");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save your note. We'll retry on your next edit.");
    }
  }, [applyNote]);

  const flushPending = useCallback((): Promise<void> => {
    inflightRef.current = inflightRef.current.then(doFlush, doFlush);
    return inflightRef.current;
  }, [doFlush]);

  const queueSave = useCallback((id: string, patch: { title?: string; content?: string }) => {
    const prev = pendingRef.current;
    pendingRef.current = { id, patch: { ...(prev && prev.id === id ? prev.patch : {}), ...patch } };
    // Optimistic mirror so the card in the list follows the keystrokes.
    const cached = cacheRef.current.get(id);
    if (cached) {
      const next: Note = { ...cached, ...patch };
      if (patch.content !== undefined) {
        const text = htmlToText(patch.content);
        next.snippet = text.slice(0, 180);
        next.word_count = countWords(text);
        next.char_count = text.length;
      }
      cacheRef.current.set(id, next);
      setList((l) => l.map((n) => (n.id === id ? summaryOf(next) : n)));
    }
    setSaveState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void flushPending(), AUTOSAVE_MS);
  }, [flushPending]);

  // Flush when the tab hides or the page unloads, and on unmount.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushPending();
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      if (pendingRef.current) {
        void flushPending();
        e.preventDefault();
        e.returnValue = "";
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
      void flushPending();
    };
  }, [flushPending]);

  // ── Selection follows the URL ────────────────────────────────────────
  useEffect(() => {
    const prevId = prevIdRef.current;
    prevIdRef.current = noteId;
    if (prevId && prevId !== noteId) {
      // A note created this session and never typed into is noise: drop it
      // silently instead of leaving "Untitled note" cards behind.
      const cached = cacheRef.current.get(prevId);
      const pend = pendingRef.current?.id === prevId ? pendingRef.current.patch : {};
      const t = pend.title ?? cached?.title ?? "";
      const c = pend.content ?? cached?.content ?? "";
      if (freshIdsRef.current.has(prevId) && !t.trim() && isBlankHtml(c) && !(cached?.tags?.length)) {
        freshIdsRef.current.delete(prevId);
        if (pendingRef.current?.id === prevId) pendingRef.current = null;
        cacheRef.current.delete(prevId);
        dropFromList(prevId);
        setCounts((cn) => ({ ...cn, all: Math.max(0, cn.all - 1) }));
        void notesApi.remove(prevId, true).catch(() => undefined);
      } else {
        void flushPending();
      }
    }
    setSaveState("idle");
    if (!noteId) {
      setSelected(null);
      return;
    }
    const cached = cacheRef.current.get(noteId);
    if (cached) {
      setSelected(cached);
      return;
    }
    let active = true;
    setNoteLoading(true);
    notesApi
      .get(noteId)
      .then((res) => {
        if (!active) return;
        cacheRef.current.set(noteId, res.data);
        setSelected(res.data);
      })
      .catch(() => {
        if (!active) return;
        toast.error("That note is no longer available.");
        navigate("/notes", { replace: true });
      })
      .finally(() => {
        if (active) setNoteLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  // ── Actions ──────────────────────────────────────────────────────────
  const openNote = useCallback((id: string) => navigate(`/notes/${encodeURIComponent(id)}`), [navigate]);
  const closeNote = useCallback(() => navigate("/notes"), [navigate]);

  const createNote = useCallback(async () => {
    await flushPending();
    try {
      const res = await notesApi.create({
        title: "",
        content: "",
        color: color || "default",
        tags: tag ? [tag] : [],
      });
      const note = res.data;
      cacheRef.current.set(note.id, note);
      freshIdsRef.current.add(note.id);
      if (scope !== "all") setScope("all");
      setList((l) => [summaryOf(note), ...l]);
      setCounts((c) => ({ ...c, all: c.all + 1 }));
      openNote(note.id);
    } catch {
      toast.error("Couldn't create a note. Please try again.");
    }
  }, [color, flushPending, openNote, scope, tag]);

  const patchMeta = useCallback(async (id: string, patch: NotePatch, opts: { toast?: string; undo?: NotePatch } = {}) => {
    const cached = cacheRef.current.get(id);
    if (cached) applyNote({ ...cached, ...patch } as Note);
    else setList((l) => l.map((n) => (n.id === id ? { ...n, ...patch } as NoteSummary : n)));
    // Leaving the current scope (archive from All, unpin from Pinned…) takes
    // the card out immediately instead of after the refresh round-trip.
    const leaves =
      (scope === "all" && patch.is_archived === true) ||
      (scope === "archived" && patch.is_archived === false) ||
      (scope === "pinned" && (patch.is_pinned === false || patch.is_archived === true));
    if (leaves) dropFromList(id);
    try {
      const res = await notesApi.update(id, patch);
      applyNote(res.data);
      void loadList({ silent: true });
      if (opts.toast) {
        toast.success(opts.toast, opts.undo ? {
          duration: UNDO_TOAST_MS,
          action: { label: "Undo", onClick: () => void patchMeta(id, opts.undo as NotePatch) },
        } : undefined);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update the note.");
      void loadList({ silent: true });
    }
  }, [applyNote, dropFromList, loadList, scope]);

  const trashNote = useCallback(async (id: string, opts: { navigateAway?: boolean } = {}) => {
    if (pendingRef.current?.id === id) await flushPending();
    const cached = cacheRef.current.get(id);
    // A never-typed-into note from this session isn't worth a Trash entry.
    const blankFresh = freshIdsRef.current.has(id)
      && !(cached?.title || "").trim() && isBlankHtml(cached?.content || "") && !(cached?.tags?.length);
    freshIdsRef.current.delete(id);
    dropFromList(id);
    if (opts.navigateAway) closeNote();
    if (blankFresh) {
      cacheRef.current.delete(id);
      await notesApi.remove(id, true).catch(() => undefined);
      void loadList({ silent: true });
      return;
    }
    try {
      await notesApi.remove(id, false);
      if (cached) cacheRef.current.set(id, { ...cached, deleted_at: new Date().toISOString(), is_pinned: false });
      void loadList({ silent: true });
      toast.success("Moved to Trash", {
        duration: UNDO_TOAST_MS,
        action: {
          label: "Undo",
          onClick: () => {
            void notesApi.restore(id).then((res) => {
              cacheRef.current.set(id, res.data);
              void loadList({ silent: true });
            });
          },
        },
      });
    } catch {
      toast.error("Couldn't move the note to Trash.");
      void loadList({ silent: true });
    }
  }, [closeNote, dropFromList, flushPending, loadList]);

  const restoreNote = useCallback(async (id: string) => {
    try {
      const res = await notesApi.restore(id);
      cacheRef.current.set(id, res.data);
      setSelected((prev) => (prev && prev.id === id ? res.data : prev));
      if (scope === "trash") dropFromList(id);
      void loadList({ silent: true });
      toast.success("Note restored");
    } catch {
      toast.error("Couldn't restore the note.");
    }
  }, [dropFromList, loadList, scope]);

  const deleteForever = useCallback((id: string, title: string) => {
    setConfirm({
      title: "Delete this note forever?",
      description: `"${title || "Untitled note"}" will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete forever",
      tone: "danger",
      onConfirm: async () => {
        try {
          await notesApi.remove(id, true);
          cacheRef.current.delete(id);
          dropFromList(id);
          if (noteId === id) closeNote();
          void loadList({ silent: true });
          toast.success("Note deleted");
        } catch {
          toast.error("Couldn't delete the note.");
        }
      },
    });
  }, [closeNote, dropFromList, loadList, noteId]);

  const emptyTrash = useCallback(() => {
    setConfirm({
      title: "Empty Trash?",
      description: `${counts.trash} ${counts.trash === 1 ? "note" : "notes"} will be permanently deleted. This cannot be undone.`,
      confirmLabel: "Empty Trash",
      tone: "danger",
      onConfirm: async () => {
        try {
          const res = await notesApi.emptyTrash();
          list.forEach((n) => cacheRef.current.delete(n.id));
          if (selected?.deleted_at) closeNote();
          void loadList({ silent: true });
          toast.success(`Trash emptied · ${res.data.removed} removed`);
        } catch {
          toast.error("Couldn't empty the Trash.");
        }
      },
    });
  }, [closeNote, counts.trash, list, loadList, selected]);

  const duplicateNote = useCallback(async (id: string) => {
    await flushPending();
    try {
      const res = await notesApi.duplicate(id);
      cacheRef.current.set(res.data.id, res.data);
      void loadList({ silent: true });
      openNote(res.data.id);
      toast.success("Duplicated — you're now editing the copy");
    } catch {
      toast.error("Couldn't duplicate the note.");
    }
  }, [flushPending, loadList, openNote]);

  // What the editor currently shows: the note plus any edit not yet saved.
  const currentText = useCallback((note: Note) => {
    const pend = pendingRef.current?.id === note.id ? pendingRef.current.patch : {};
    return { title: pend.title ?? note.title, content: pend.content ?? note.content };
  }, []);

  const sendToPlanner = useCallback(async (note: Note) => {
    const { title, content } = currentText(note);
    const text = htmlToText(content);
    const heading = title.trim() || text.split("\n")[0]?.slice(0, 120) || "Note";
    // The note body goes into `description`: that is the field the planner
    // card renders under the title (TodoCard shows `description`, not `notes`).
    // An untitled note used its first line as the title, so skip that line.
    const body = title.trim() ? text : text.split("\n").slice(1).join("\n").trim();
    try {
      await todoApi.quickAdd({ title: heading, description: body.slice(0, 5000) || undefined });
      toast.success("Added to today's Daily Planner", {
        action: { label: "Open planner", onClick: () => navigate("/todo") },
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't add this to the Daily Planner.");
    }
  }, [currentText, navigate]);

  // From a card the body is not loaded yet: use the cache or fetch it.
  const sendSummaryToPlanner = useCallback(async (summary: NoteSummary) => {
    let note = cacheRef.current.get(summary.id);
    if (!note) {
      try {
        note = (await notesApi.get(summary.id)).data;
        cacheRef.current.set(note.id, note);
      } catch {
        toast.error("Couldn't open this note.");
        return;
      }
    }
    await sendToPlanner(note);
  }, [sendToPlanner]);

  const shareSaved = useCallback((id: string, sharedWith: NoteShareUser[]) => {
    const cached = cacheRef.current.get(id);
    if (cached) applyNote({ ...cached, shared_with: sharedWith });
    else setList((prev) => prev.map((n) => (n.id === id ? { ...n, shared_with: sharedWith } : n)));
  }, [applyNote]);

  const handleAction = useCallback(async (action: NoteAction) => {
    if (!selected) return;
    const { title, content } = currentText(selected);
    switch (action) {
      case "duplicate":
        return duplicateNote(selected.id);
      case "copy": {
        const text = [title.trim(), htmlToText(content)].filter(Boolean).join("\n\n");
        try {
          await navigator.clipboard.writeText(text);
          toast.success("Copied to clipboard");
        } catch {
          toast.error("Clipboard access was blocked by the browser.");
        }
        return;
      }
      case "download": {
        const md = `# ${title.trim() || "Untitled note"}\n\n${htmlToMarkdown(content)}\n`;
        downloadTextFile(`${fileSafeName(title)}.md`, md, "text/markdown");
        toast.success("Downloaded as Markdown");
        return;
      }
      case "print":
        await flushPending();
        window.print();
        return;
      case "planner":
        return sendToPlanner(selected);
      case "share":
        await flushPending();
        setShareFor(summaryOf(selected));
        return;
      case "trash":
        return trashNote(selected.id, { navigateAway: true });
      case "restore":
        return restoreNote(selected.id);
      case "deleteForever":
        return deleteForever(selected.id, title);
      default:
        return undefined;
    }
  }, [selected, currentText, duplicateNote, flushPending, sendToPlanner, trashNote, restoreNote, deleteForever]);

  // ── Bulk (multi-select) ──────────────────────────────────────────────
  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const runBulk = useCallback(async (action: "pin" | "unpin" | "archive" | "unarchive" | "trash" | "restore") => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    try {
      await notesApi.bulk(ids, action);
      ids.forEach((id) => cacheRef.current.delete(id));
      if (selected && ids.includes(selected.id) && (action === "trash" || action === "archive")) closeNote();
      setChecked(new Set());
      await loadList({ silent: true });
      const verb = { pin: "pinned", unpin: "unpinned", archive: "archived", unarchive: "unarchived", trash: "moved to Trash", restore: "restored" }[action];
      toast.success(`${ids.length} ${ids.length === 1 ? "note" : "notes"} ${verb}`);
    } catch {
      toast.error("Couldn't apply that to the selected notes.");
    }
  }, [checked, closeNote, loadList, selected]);

  const bulkDeleteForever = useCallback(() => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    setConfirm({
      title: `Delete ${ids.length} ${ids.length === 1 ? "note" : "notes"} forever?`,
      description: "They will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete forever",
      tone: "danger",
      onConfirm: async () => {
        await Promise.allSettled(ids.map((id) => notesApi.remove(id, true)));
        ids.forEach((id) => cacheRef.current.delete(id));
        if (selected && ids.includes(selected.id)) closeNote();
        setChecked(new Set());
        await loadList({ silent: true });
        toast.success("Deleted");
      },
    });
  }, [checked, closeNote, loadList, selected]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────
  const latest = useRef({ createNote, flushPending, closeNote, noteId });
  latest.current = { createNote, flushPending, closeNote, noteId };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void latest.current.flushPending();
        return;
      }
      if (mod && e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        void latest.current.createNote();
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (e.key === "n" && !mod && !e.altKey) {
        e.preventDefault();
        void latest.current.createNote();
      } else if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "?") {
        setShowShortcuts((s) => !s);
      } else if (e.key === "Escape") {
        if (showShortcuts) setShowShortcuts(false);
        else if (latest.current.noteId) latest.current.closeNote();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showShortcuts]);

  // ── Derived ──────────────────────────────────────────────────────────
  const sortedList = useMemo(() => {
    const arr = [...list];
    if (sort === "title") {
      arr.sort((a, b) => (a.title || "Untitled note").localeCompare(b.title || "Untitled note", undefined, { sensitivity: "base" }));
    } else if (sort === "created") {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr; // "updated": server order (pinned first, last edited first)
  }, [list, sort]);

  const scopeLabel = SCOPES.find((s) => s.key === scope)?.label || "Notes";
  const filterActive = !!(debouncedQuery || tag || color);
  // The open note is a popup over the gallery, so the list keeps its own view.
  const listVariant: "grid" | "column" = view === "grid" ? "grid" : "column";
  const tagNames = useMemo(() => tags.map((t) => t.name), [tags]);
  // Shared-with-me notes are editable (title/body); only Trash is read-only.
  const readOnly = !!selected?.deleted_at;
  // A blank note created this session opens with the title focused.
  const autoFocusTitle = !!(selected && freshIdsRef.current.has(selected.id));

  const clearFilters = () => {
    setQuery("");
    setTag(null);
    setColor(null);
  };

  // ── Render ───────────────────────────────────────────────────────────
  const emptyState = () => {
    if (filterActive) {
      return (
        <div className="nm-empty">
          <span className="nm-empty-icon"><Search size={24} aria-hidden="true" /></span>
          <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">No notes match</h3>
          <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">Try another word, or clear the filters.</p>
          <button type="button" className="gt-btn is-ghost mt-4" onClick={clearFilters}>Clear filters</button>
        </div>
      );
    }
    if (scope === "trash") {
      return (
        <div className="nm-empty">
          <span className="nm-empty-icon"><Trash2 size={24} aria-hidden="true" /></span>
          <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Trash is empty</h3>
          <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">Deleted notes wait here until you empty the Trash.</p>
        </div>
      );
    }
    if (scope === "archived") {
      return (
        <div className="nm-empty">
          <span className="nm-empty-icon"><Archive size={24} aria-hidden="true" /></span>
          <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Nothing archived</h3>
          <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">Archive notes you're done with to keep your list tidy.</p>
        </div>
      );
    }
    if (scope === "shared") {
      return (
        <div className="nm-empty">
          <span className="nm-empty-icon"><Users size={24} aria-hidden="true" /></span>
          <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Nothing shared with you yet</h3>
          <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">Notes colleagues share with you appear here, and you can edit them.</p>
        </div>
      );
    }
    if (scope === "pinned") {
      return (
        <div className="nm-empty">
          <span className="nm-empty-icon"><Pin size={24} aria-hidden="true" /></span>
          <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">No pinned notes</h3>
          <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">Pin the notes you reach for most and they'll stay on top.</p>
        </div>
      );
    }
    return (
      <div className="nm-empty">
        <span className="nm-empty-icon"><NotebookPen size={26} aria-hidden="true" /></span>
        <h3 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Your notebook is empty</h3>
        <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
          Jot down meeting notes, ideas and checklists. Notes are private until you share them, and save as you type.
        </p>
        <button type="button" className="gt-btn is-primary mt-5" onClick={() => void createNote()}>
          <Plus size={16} aria-hidden="true" /> Write your first note
        </button>
      </div>
    );
  };

  return (
    <div
      className="nm-page gt-shell"
      data-testid="notemaker-page"
    >
      <header className="nm-header">
        <div className="nm-header-title">
          <p className="gt-eyebrow">Task management / Notemaker</p>
          <h1 className="gt-page-title" style={{ lineHeight: 1.05, marginTop: 2 }}>
            Note<em>maker</em>
          </h1>
        </div>
        <div className="nm-header-actions">
          <div className="nm-search-wrap">
            <Search size={16} aria-hidden="true" />
            <input
              ref={searchRef}
              className="gt-input is-search"
              type="search"
              value={query}
              placeholder="Search notes…"
              aria-label="Search notes"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setQuery("");
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            {query ? (
              <button type="button" className="nm-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
                <X size={14} aria-hidden="true" />
              </button>
            ) : (
              <kbd className="gt-kbd nm-search-kbd" aria-hidden="true">/</kbd>
            )}
          </div>
          <button
            type="button"
            className="gt-icon-btn nm-shortcuts-btn"
            onClick={() => setShowShortcuts((s) => !s)}
            aria-label="Keyboard shortcuts"
            aria-pressed={showShortcuts}
            title="Keyboard shortcuts (?)"
          >
            <Keyboard size={18} aria-hidden="true" />
          </button>
          <button type="button" className="gt-btn is-primary" onClick={() => void createNote()} data-testid="new-note">
            <Plus size={16} aria-hidden="true" /> New note <span className="gt-btn-kbd" aria-hidden="true">N</span>
          </button>
        </div>
      </header>

      {showShortcuts && (
        <div className="gt-card is-flat gt-pop-in" style={{ padding: "12px 16px" }} role="dialog" aria-label="Keyboard shortcuts">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-[var(--gt-text-secondary)]">
            <span><kbd className="gt-kbd">N</kbd> New note</span>
            <span><kbd className="gt-kbd">/</kbd> Search</span>
            <span><kbd className="gt-kbd">Esc</kbd> Close note</span>
            <span><kbd className="gt-kbd">{MOD}</kbd>+<kbd className="gt-kbd">S</kbd> Save now</span>
            <span><kbd className="gt-kbd">{MOD}</kbd>+<kbd className="gt-kbd">B</kbd>/<kbd className="gt-kbd">I</kbd>/<kbd className="gt-kbd">U</kbd> Bold / Italic / Underline</span>
            <span><kbd className="gt-kbd">{MOD}</kbd>+<kbd className="gt-kbd">K</kbd> Link</span>
            <span><kbd className="gt-kbd">{MOD}</kbd>+<kbd className="gt-kbd">Enter</kbd> Tick checklist item</span>
            <span><kbd className="gt-kbd">-</kbd>, <kbd className="gt-kbd">1.</kbd>, <kbd className="gt-kbd">[]</kbd>, <kbd className="gt-kbd">#</kbd> + space → list / numbers / checklist / heading</span>
            <button type="button" className="gt-icon-btn is-sm ml-auto" onClick={() => setShowShortcuts(false)} aria-label="Close shortcuts">
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Scope chips — compact count pills (same shape as the Tasks page
          status chips) that double as the scope filter. */}
      <div className="nm-chips" role="group" aria-label="Note counts">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`nm-chip is-${s.tone}${scope === s.key ? " is-active" : ""}`}
            aria-pressed={scope === s.key}
            onClick={() => setScope(s.key)}
            title={s.hint}
            data-testid={`stat-${s.key}`}
          >
            <span className="nm-chip-icon">{s.icon}</span>
            <span className="nm-chip-text">
              <span className="nm-chip-label">{s.label}</span>
              <span className="nm-chip-value">{counts[s.countKey]}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Filter row: tags + colour on the left, sort + view on the right. */}
      <div className="nm-filters" role="group" aria-label="Filters">
        <div className="nm-filters-left">
          {tags.length > 0 && (
            <div className="nm-tag-cloud" aria-label="Filter by tag">
              {tags.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  className={`nm-tag-chip${tag === t.name ? " is-active" : ""}`}
                  aria-pressed={tag === t.name}
                  onClick={() => setTag(tag === t.name ? null : t.name)}
                >
                  #{t.name} <span className="nm-tag-count">{t.count}</span>
                </button>
              ))}
            </div>
          )}
          <div className="nm-swatch-row" role="group" aria-label="Filter by colour">
            {NOTE_SWATCHES.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`nm-swatch${color === s.key ? " is-active" : ""}`}
                style={{ background: s.key === "default" ? "#fff" : s.tint, boxShadow: `inset 0 0 0 2px ${s.stripe}` }}
                title={`${s.label} notes`}
                aria-label={`${s.label} notes`}
                aria-pressed={color === s.key}
                onClick={() => setColor(color === s.key ? null : s.key)}
              />
            ))}
          </div>
          {filterActive && (
            <button type="button" className="gt-btn is-ghost is-sm" onClick={clearFilters}>
              <X size={12} aria-hidden="true" /> Clear filters
            </button>
          )}
        </div>
        <div className="nm-filters-right">
          <select
            className="nm-select"
            value={sort}
            aria-label="Sort notes"
            onChange={(e) => { setSort(e.target.value as NoteSort); writePref(SORT_KEY, e.target.value); }}
          >
            <option value="updated">Last edited</option>
            <option value="created">Newest first</option>
            <option value="title">Title A–Z</option>
          </select>
          <div className="nm-segment" role="group" aria-label="View">
            <button type="button" className={view === "grid" ? "is-active" : ""} aria-label="Grid view" aria-pressed={view === "grid"} onClick={() => { setView("grid"); writePref(VIEW_KEY, "grid"); }}>
              <LayoutGrid size={15} aria-hidden="true" />
            </button>
            <button type="button" className={view === "list" ? "is-active" : ""} aria-label="List view" aria-pressed={view === "list"} onClick={() => { setView("list"); writePref(VIEW_KEY, "list"); }}>
              <Rows3 size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="nm-body">
        {/* List */}
        <section className={`nm-list-pane${checked.size ? " is-selecting" : ""}`} aria-label={scopeLabel}>
          {checked.size > 0 ? (
            <div className="nm-bulk-bar" role="toolbar" aria-label="Bulk actions">
              <span>{checked.size} selected</span>
              {scope === "trash" ? (
                <>
                  <button type="button" className="gt-btn is-ghost" onClick={() => void runBulk("restore")}><RotateCcw size={13} aria-hidden="true" /> Restore</button>
                  <button type="button" className="gt-btn is-ghost" onClick={bulkDeleteForever}><Trash2 size={13} aria-hidden="true" /> Delete forever</button>
                </>
              ) : (
                <>
                  {scope !== "archived" && (
                    <button type="button" className="gt-btn is-ghost" onClick={() => void runBulk(scope === "pinned" ? "unpin" : "pin")}>
                      {scope === "pinned" ? <PinOff size={13} aria-hidden="true" /> : <Pin size={13} aria-hidden="true" />} {scope === "pinned" ? "Unpin" : "Pin"}
                    </button>
                  )}
                  <button type="button" className="gt-btn is-ghost" onClick={() => void runBulk(scope === "archived" ? "unarchive" : "archive")}>
                    {scope === "archived" ? <ArchiveRestore size={13} aria-hidden="true" /> : <Archive size={13} aria-hidden="true" />} {scope === "archived" ? "Unarchive" : "Archive"}
                  </button>
                  <button type="button" className="gt-btn is-ghost" onClick={() => void runBulk("trash")}><Trash2 size={13} aria-hidden="true" /> Trash</button>
                </>
              )}
              <button type="button" className="gt-btn is-ghost" style={{ marginLeft: "auto" }} onClick={() => setChecked(new Set())}>Clear</button>
            </div>
          ) : (
            <div className="nm-list-toolbar">
              <div className="nm-list-toolbar-left">
                <span className="nm-list-title">{scopeLabel}</span>
                <span className="nm-list-meta">{listLoading ? "…" : `${sortedList.length} ${sortedList.length === 1 ? "note" : "notes"}`}</span>
              </div>
              {scope === "trash" && counts.trash > 0 && (
                <button type="button" className="gt-btn is-danger is-sm" onClick={emptyTrash}>Empty Trash</button>
              )}
            </div>
          )}

          <div className="nm-scroll">
            {listLoading ? (
              <div className={listVariant === "grid" ? "nm-grid" : "nm-column"} aria-busy="true" aria-label="Loading notes">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="nm-skeleton" />)}
              </div>
            ) : listError ? (
              <div className="nm-empty" role="alert">
                <span className="nm-empty-icon"><RotateCcw size={22} aria-hidden="true" /></span>
                <h3 className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Couldn't load notes</h3>
                <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">{listError}</p>
                <button type="button" className="gt-btn is-primary mt-4" onClick={() => void loadList()}>Try again</button>
              </div>
            ) : sortedList.length === 0 ? (
              emptyState()
            ) : (
              <div className={view === "list" ? "nm-column is-wide" : undefined}>
                <NoteList
                  notes={sortedList}
                  scope={scope}
                  variant={listVariant}
                  selectedId={noteId || null}
                  checkedIds={checked}
                  onOpen={openNote}
                  onToggleCheck={toggleCheck}
                  onPin={(n) => void patchMeta(n.id, { is_pinned: !n.is_pinned })}
                  onArchive={(n) => void patchMeta(n.id, { is_archived: !n.is_archived }, {
                    toast: n.is_archived ? "Note unarchived" : "Note archived",
                    undo: { is_archived: n.is_archived },
                  })}
                  onTrash={(n) => void trashNote(n.id, { navigateAway: noteId === n.id })}
                  onRestore={(n) => void restoreNote(n.id)}
                  onDeleteForever={(n) => deleteForever(n.id, n.title)}
                  onShare={(n) => { void flushPending(); setShareFor(n); }}
                  onPlanner={(n) => void sendSummaryToPlanner(n)}
                  onDuplicate={(n) => void duplicateNote(n.id)}
                />
              </div>
            )}
          </div>
        </section>

      </div>

      {/* Editor — a large popup over the gallery (full screen on phones), so a
          note with a big table gets the whole window. Portalled to <body>
          so no ancestor's overflow/transform clips it; the wrapper repeats
          `nm-page gt-shell` so the page-scoped styles still apply. Clicking
          the dim backdrop or ✕ closes it (edits are saved on close). */}
      {noteId && createPortal(
        <div
          className="nm-page gt-shell nm-note-layer"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeNote(); }}
        >
          <section className="nm-editor-pane nm-note-popup" role="dialog" aria-modal="true" aria-label="Note">
            {selected && selected.id === noteId ? (
              <NoteEditor
                key={selected.id}
                note={selected}
                saveState={saveState}
                tagSuggestions={tagNames}
                readOnly={readOnly}
                autoFocusTitle={autoFocusTitle}
                onChange={(patch) => queueSave(selected.id, patch)}
                onMeta={(patch) => void patchMeta(selected.id, patch, patch.is_archived !== undefined ? {
                  toast: patch.is_archived ? "Note archived" : "Note unarchived",
                  undo: { is_archived: !patch.is_archived },
                } : undefined)}
                onAction={(a) => void handleAction(a)}
                onSaveNow={() => flushPending()}
                onBack={closeNote}
              />
            ) : (
              <div className="nm-editor" aria-busy={noteLoading}>
                <div className="nm-empty" style={{ margin: "auto" }}>
                  <Loader2 size={26} className="animate-spin motion-reduce:animate-none text-[var(--gt-primary)]" aria-hidden="true" />
                  <p className="mt-3 text-sm text-[var(--gt-text-secondary)]">Opening note…</p>
                </div>
              </div>
            )}
          </section>
        </div>,
        document.body
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      {shareFor && (
        <ShareNoteDialog
          note={shareFor}
          onSaved={shareSaved}
          onClose={() => setShareFor(null)}
        />
      )}
    </div>
  );
}
